const SHEETS_API_BASE_URL = 'https://sheets.googleapis.com/v4/spreadsheets';
const DRIVE_API_BASE_URL = 'https://www.googleapis.com/drive/v3/files';

class GoogleSheetsService {
  constructor() {
    this.accessToken = null;
  }

  setAccessToken(token) {
    this.accessToken = token;
  }

  getHeaders(includeContentType = false) {
    if (!this.accessToken) throw new Error('Access token not set. Please authenticate first.');
    const headers = { Authorization: `Bearer ${this.accessToken}` };
    if (includeContentType) headers['Content-Type'] = 'application/json';
    return headers;
  }

  async handleResponse(response) {
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = errorData.error?.message || `HTTP error ${response.status}`;
      if (response.status === 401) throw new Error('Authentication failed. Please sign in again.');
      if (response.status === 403) throw new Error('Access denied. Please check your permissions.');
      throw new Error(message);
    }
    return response.json();
  }

  async verifySpreadsheet(spreadsheetId) {
    try {
      const res = await fetch(`${SHEETS_API_BASE_URL}/${spreadsheetId}`, { headers: this.getHeaders() });
      return res.ok;
    } catch (e) {
      return false;
    }
  }

  async createSpreadsheet(title, sheetName = 'Expenses') {
    const body = {
      properties: { title },
      sheets: [{ properties: { title: sheetName, gridProperties: { rowCount: 1000, columnCount: 10 } } }]
    };
    const res = await fetch(SHEETS_API_BASE_URL, { method: 'POST', headers: this.getHeaders(true), body: JSON.stringify(body) });
    const data = await this.handleResponse(res);
    return { spreadsheetId: data.spreadsheetId, spreadsheetUrl: data.spreadsheetUrl };
  }

  async addHeaders(spreadsheetId, headers, sheetName = 'Expenses') {
    const range = `${sheetName}!A1:${String.fromCharCode(64 + headers.length)}1`;
    const res = await fetch(`${SHEETS_API_BASE_URL}/${spreadsheetId}/values/${encodeURIComponent(range)}?valueInputOption=RAW`, {
      method: 'PUT',
      headers: this.getHeaders(true),
      body: JSON.stringify({ values: [headers] })
    });
    return await this.handleResponse(res);
  }

  async appendRow(spreadsheetId, values, sheetName = 'Expenses') {
    const range = `${sheetName}!A1`;
    const res = await fetch(`${SHEETS_API_BASE_URL}/${spreadsheetId}/values/${encodeURIComponent(range)}:append?valueInputOption=RAW&insertDataOption=INSERT_ROWS`, {
      method: 'POST',
      headers: this.getHeaders(true),
      body: JSON.stringify({ values: [values] })
    });
    return await this.handleResponse(res);
  }

  async getSheetData(spreadsheetId, range, sheetName = 'Expenses') {
    const fullRange = `${sheetName}!${range}`;
    const res = await fetch(`${SHEETS_API_BASE_URL}/${spreadsheetId}/values/${encodeURIComponent(fullRange)}`, {
      headers: this.getHeaders()
    });
    return await this.handleResponse(res);
  }

  async getSheetLastRow(spreadsheetId, sheetName = 'Expenses') {
    try {
      const res = await fetch(`${SHEETS_API_BASE_URL}/${spreadsheetId}/values/${encodeURIComponent(sheetName + '!A:A')}`, {
        headers: this.getHeaders()
      });
      const data = await this.handleResponse(res);
      if (!data || !data.values) return 1;
      return data.values.length + 0; // A:A includes header row; caller expects last row number (header at row1)
    } catch (err) {
      return null;
    }
  }

  async updateRange(spreadsheetId, range, values, sheetName = 'Expenses') {
    const fullRange = `${sheetName}!${range}`;
    const res = await fetch(`${SHEETS_API_BASE_URL}/${spreadsheetId}/values/${encodeURIComponent(fullRange)}?valueInputOption=RAW`, {
      method: 'PUT',
      headers: this.getHeaders(true),
      body: JSON.stringify({ values })
    });
    return await this.handleResponse(res);
  }

  async getSpreadsheetMetadata(spreadsheetId) {
    const res = await fetch(`${SHEETS_API_BASE_URL}/${spreadsheetId}`, { headers: this.getHeaders() });
    return await this.handleResponse(res);
  }

  async clearRange(spreadsheetId, range, sheetName = 'Expenses') {
    const fullRange = `${sheetName}!${range}`;
    const res = await fetch(`${SHEETS_API_BASE_URL}/${spreadsheetId}/values/${encodeURIComponent(fullRange)}:clear`, {
      method: 'POST',
      headers: this.getHeaders(true)
    });
    return await this.handleResponse(res);
  }

  async batchUpdate(spreadsheetId, body) {
    const res = await fetch(`${SHEETS_API_BASE_URL}/${spreadsheetId}:batchUpdate`, {
      method: 'POST',
      headers: this.getHeaders(true),
      body: JSON.stringify(body)
    });
    return await this.handleResponse(res);
  }

  async deleteRow(spreadsheetId, sheetId, rowNumber) {
    const startIndex = rowNumber - 1;
    const endIndex = rowNumber;
    const body = {
      requests: [
        {
          deleteDimension: {
            range: { sheetId: sheetId, dimension: 'ROWS', startIndex, endIndex }
          }
        }
      ]
    };
    return await this.batchUpdate(spreadsheetId, body);
  }

  async searchSpreadsheetByName(name) {
    try {
      const q = `name = '${name.replace(/'/g, "\\'")}' and mimeType = 'application/vnd.google-apps.spreadsheet' and trashed = false`;
      const res = await fetch(`${DRIVE_API_BASE_URL}?q=${encodeURIComponent(q)}&fields=files(id,name)&pageSize=10`, {
        headers: this.getHeaders()
      });
      const data = await this.handleResponse(res);
      console.log('data', data);
      if (data && data.files && data.files.length > 0) {
        const file = data.files[0];
        const meta = await this.getSpreadsheetMetadata(file.id);
        const sheetName = (meta.sheets && meta.sheets[0] && meta.sheets[0].properties && meta.sheets[0].properties.title) || 'Categories';
        return { spreadsheetId: file.id, sheetName };
      }
      return null;
    } catch (err) {
      return null;
    }
  }

  async searchFinanceTrackerYears() {
    try {
      const q = `name contains 'Finance Tracker' and mimeType = 'application/vnd.google-apps.spreadsheet' and trashed = false`;
      const res = await fetch(`${DRIVE_API_BASE_URL}?q=${encodeURIComponent(q)}&fields=files(id,name)&pageSize=100`, {
        headers: this.getHeaders()
      });
      const data = await this.handleResponse(res);

      if (data && data.files && data.files.length > 0) {
        const years = new Set();

        // Extract years from filenames like "Finance Tracker 2024", "Finance Tracker 2025", etc.
        data.files.forEach(file => {
          const match = file.name.match(/Finance Tracker (\d{4})/);
          if (match && match[1]) {
            const year = parseInt(match[1], 10);
            // Only add valid years (e.g., 2000-2099)
            if (year >= 2000 && year <= 2099) {
              years.add(year);
            }
          }
        });

        // Convert to sorted array (most recent first)
        return Array.from(years).sort((a, b) => b - a);
      }

      return [];
    } catch (err) {
      console.error('Error searching Finance Tracker years:', err);
      return [];
    }
  }
}

export const googleSheetsService = new GoogleSheetsService();
export default googleSheetsService;
